import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { ITableConfigurableModules } from '../../types/Itable';
import FileInput from '../../uikit/fileInput/FileInput';
import { JsonifiedFile } from '../../utility/functions';

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  templateUrl: string;
  module: ITableConfigurableModules;
  onUpload: (fileBase64: string) => void;
  processing?: boolean;
}

export default function ImportModal({ open, onClose, templateUrl, module, onUpload, processing = false }: ImportModalProps) {
  const [file, setFile] = useState<JsonifiedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setError(null);
    setInputKey((k) => k + 1);
  }, [open]);

  const handleFilesChange = (files: JsonifiedFile[]) => {
    if (files.length > 1) {
      setFile(null);
      setError('Please upload only one file.');
      return;
    }

    const selected = files[0];

    if (!selected) {
      setFile(null);
      setError(null);
      return;
    }

    if (!selected.name.toLowerCase().endsWith('.csv')) {
      setFile(null);
      setError('Only CSV files are allowed.');
      return;
    }

    setError(null);
    setFile(selected);
  };
  const handleUpload = () => {
    if (!file || processing) return;
    onUpload(file.contents);
  };

  return (
    <Dialog open={open} onClose={processing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import {_.startCase(module)}</DialogTitle>

      <DialogContent dividers>
        <Typography fontWeight={600} mb={1}>
          Step 1: Prepare your file
        </Typography>

        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2">
              Download the{' '}
              <Box component="a" href={templateUrl} download sx={{ fontWeight: 600 }}>
                {_.startCase(module)}_template.xlsx
              </Box>
            </Typography>
          </li>

          <li>
            <Typography variant="body2">Open in Excel/Google Sheets and add your data (do not change header row).</Typography>
          </li>

          <li>
            <Typography variant="body2">Save the file as .csv when you are done.</Typography>
          </li>
        </Box>

        <Typography fontWeight={600} mb={1}>
          Step 2: Upload your file
        </Typography>

        <FileInput key={inputKey} onFilesChange={handleFilesChange} />

        {error && (
          <Typography color="error" variant="body2" mt={1}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>

        <Button variant="contained" disabled={!file || processing} onClick={handleUpload}>
          {processing ? 'Uploading…' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
